import {Inject, Injectable} from '@angular/core';
import {AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument} from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';

import {firstValueFrom, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import * as _ from 'underscore';
import {AventiFile} from '../models/File';
import {Project} from '../models/Project';
import {ProjectAccess, User} from '../models/User';
import {DbpathService} from './dbpath.service';
import {IdService} from './id.service';
import {SharedService} from './shared.service';
import {UserService} from './user.service';
import {SortService} from './sort.service';
import {Timestamp} from 'firebase/firestore';
import {FirebaseApp} from '@angular/fire/app';
import {getFunctions, httpsCallable} from '@angular/fire/functions';

export interface RegenerateProjectPdfsRequest {
  projectId: string;
  maxImageSizeKB: number;
  mode: 'append' | 'overwrite-latest';
  user: any;
}

export interface RegenerateProjectPdfsResponse {
  success: boolean;
  totalCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  projectCol: AngularFirestoreCollection<Project>;
  projectDoc: AngularFirestoreDocument<Project>;
  projectObs: Observable<Project>;
  projectsObs: Observable<Project[]>;

  project: Project;
  private regenerateProjectPdfsFn: (
    data: RegenerateProjectPdfsRequest
  ) => Promise<{ data: RegenerateProjectPdfsResponse }>;

  constructor(
    @Inject(FirebaseApp) private app: FirebaseApp,
    private afs: AngularFirestore,
    private idservice: IdService,
    private dbpathservice: DbpathService,
    private sharedservice: SharedService,
    private userservice: UserService,
    private sortservice: SortService
  ) {
    this.project = new Project();
    const functions = getFunctions(this.app, 'europe-west1');
    this.regenerateProjectPdfsFn = httpsCallable<
      RegenerateProjectPdfsRequest,
      RegenerateProjectPdfsResponse
    >(functions, 'regenerateProjectPdfs');
    this.projectCol = this.afs.collection('projects', (ref) => {
      return ref.orderBy('no');
    }); // reference
  }

  getAllShortProjects(user?: User) {
    // return this.projectCol.valueChanges({ idField: 'id' });

    return this.projectCol.snapshotChanges().pipe(
      map((actions) => {
        const userProjectIds = user?.projectaccess?.map(projectAccess => projectAccess.id);
        const filteredActions = actions.filter(action => !user || userProjectIds?.includes(action.payload.doc.id));
        return filteredActions.map((action) => {
          const id = action.payload.doc.id;
          const project = action.payload.doc.data();
          const fromCache = (action.payload.doc as firebase.firestore.DocumentSnapshot).metadata.fromCache;
          return {id, fromCache, name: project.name, no: project.no, useStatusLevel: project.useStatusLevel} as Project;
        });
      }));
  }

  getAllProjects() {
    return this.projectCol.snapshotChanges().pipe(
      map((actions) => {
        return actions.map((action) => {
          const id = action.payload.doc.id;
          const project = action.payload.doc.data();
          const fromCache = (action.payload.doc as firebase.firestore.DocumentSnapshot).metadata.fromCache;
          return {id, fromCache, ...project} as Project;
        });
      }));
  }

  getAllProjectIds() {
    return this.projectCol.snapshotChanges().pipe(
      map((actions) => {
        return actions.map((action) => {
          return action.payload.doc.id;
        });
      }));
  }

  getChecklistStatusForProject(projectId: string) {
    // return this.projectCol.valueChanges({ idField: 'id' });

    return this.afs.doc('projects/' + projectId).snapshotChanges().pipe(
      map((action) => {

        const project = action.payload.data() as Project;
        const id = action.payload.id;
        return {id, checklistStatus: project.checklistStatus};
      }));
  }

  getFullProjectById(projectId: string): Observable<Project> {
    const doc: AngularFirestoreDocument<Project> = this.afs.doc('projects/' + projectId);
    return doc.snapshotChanges().pipe(
      map((action) => {
        const project = action.payload.data() as Project;
        const id = action.payload.id;
        return {id, ...project};
      }));
    // // const objectsObs = this.objectservice.getObjects();
    // this.projectDoc = this.afs.doc('projects/' + projectId);
    // this.projectObs = this.projectDoc.valueChanges();
    // return (this.projectObs = this.projectDoc.snapshotChanges().pipe(
    //   map((actions) => {
    //     const data = actions.payload.data() as Project;
    //     const id = actions.payload.id;
    //     const fullurl = '/p/' + id + '/prosjekt';
    //     // return { id, fullurl, ...data };
    //     if (data) {
    //       data.id = id;
    //       data.fullurl = fullurl;
    //     }
    //     return data;
    //   })
    //   // flatMap((actions) => {
    //   //   const data = actions.payload.data() as Project;
    //   //   const id = actions.payload.id;
    //   //   const fullurl = '/p/' + id + '/prosjekt';
    //   //   // return { id, fullurl, ...data };
    //   //   data.id = id;
    //   //   data.fullurl = fullurl;
    //   //   return objectsObs.pipe(
    //   //     map((c) => {
    //   //       // data.forEach((object) => {
    //   //       // object.checklists = [];
    //   //       c.forEach((object) => {
    //   //         if (object) {
    //   //           // if (object.id === checklist.parentid) {
    //   //           data.objects.push(object);
    //   //           // }
    //   //         }
    //   //       });
    //   //       // });
    //   //       return data;
    //   //     })
    //   //   );
    //   // })
    // ));
    // // return this.projectObs;
  }

  getProjectFiles(projectid: string): Observable<AventiFile[]> {
    return this.afs
      .collection(
        this.dbpathservice.dbPathProjectFiles(),
        (ref) =>
          ref
            .where('projectid', '==', projectid)
            .orderBy('name')
      )
      .valueChanges({idField: 'id'});
  }


  async createProject(project: Project) {
    const user = await this.getUserAndSetEditedBy(project);

    project.useStatusLevel = true;
    console.log(project);
    await this.afs
      .doc('projects/' + project.no)
      .set(project)
      .then(async () => {
        const projectaccess: ProjectAccess = {
          id: project.no,
          name: project.name,
        };
        await this.userservice.addProjectAccess(user, projectaccess);
      });

    return; // .add(project);
  }

  readProjects() {
    return this.afs
      .collection('projects')
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as Project;
            const id = a.payload.doc.id;
            return {id, ...data} as Project;
          });
        })
      );
  }

  readProject() {
    return this.afs
      .doc(this.dbpathservice.dbPathProject())
      .snapshotChanges()
      .pipe(
        map((actions) => {
          const project: Project = actions.payload.data();

          project.id = actions.payload.id;
          return project;
        })
      );
  }

  async updateProject(project: Partial<Project>) {
    await this.getUserAndSetEditedBy(project);

    project = _.omit(project, 'id');
    await this.afs.doc('projects/' + this.idservice.projectId()).update(project);
  }

  async regenerateProjectPdfs(
    projectId: string,
    maxImageSizeKB: number,
    user: User
  ): Promise<RegenerateProjectPdfsResponse> {
    const response = await this.regenerateProjectPdfsFn({
      projectId,
      maxImageSizeKB: Number(maxImageSizeKB),
      mode: 'overwrite-latest',
      user: {
        id: user?.id || null,
        email: user?.email || null,
        name: `${user?.firstname || ''} ${user?.lastname || ''}`.trim(),
      },
    });
    return response.data;
  }

  private async getUserAndSetEditedBy(project: Project) {
    const user = (await firstValueFrom(this.sharedservice.user));
    const timestamp = Timestamp.now();
    const usertimestamp = {
      user,
      timestamp,
    };
    if (!project.editedBy) {
      project.editedBy = [];
    }
    project.editedBy.push(usertimestamp);
    project.editedBy = this.sortservice.reduceArrayToMaxLength(project.editedBy, 'timestamp');

    return user;
  }

// deleteProject(projectid) {
  //   this.afs.doc('projects/' + projectid).delete();
  // }

  getProjectById(projectId: string): Observable<Project> {
    const doc: AngularFirestoreDocument<Project> = this.afs.doc('projects/' + projectId);
    return doc.snapshotChanges().pipe(
      map((action) => {
        const project = action.payload.data() as Project;
        const id = action.payload.id;
        return {id, ...project};
      })
    );
  }
}
